import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import styled, { css } from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import { CountTagItem, TagText } from "components/atoms/Tag";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button";
import Checkbox from "components/atoms/input/Checkbox";
import SelectBox from "components/atoms/input/SelectBox";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import { BodyWithBreadcrumb, Breadcrumb, BreadcrumbTitle } from "components/molecules/Breadcrumb";
import Table from "components/molecules/table/Table";
import {
    compareColumns,
    getColumnOrder,
    getColumnSize,
    getColumnVisibility,
    updateColumnOrder,
} from "components/molecules/table/TableManager";
import NavigatorFolderEditModal from "components/page/modal/NavigatorFolderEditModal";
import NavigatorTagMultiAddModal from "components/page/modal/NavigatorTagMultiAddModal";
import NoteAddModal from "components/page/modal/NoteAddModal";
import LoginUserLayout from "components/templates/LoginUserLayout";

import { useNoteBulkDelete, useNoteUpdate, useNotes } from "hooks/queries/useNote";
import { useUser } from "hooks/queries/useUser";
import { useStableQueryData } from "hooks/useStableQueryData";

import { useDevice } from "utils/device";
import { isReadonlyUser } from "utils/user";
import { genericEnterKeyDown } from "utils/validation";

import moment from "moment/moment";

const HeaderLeftWrap = styled.div`
    display: flex;
    align-items: end;
    gap: 8px;
`;

const HeaderRightWrap = styled.div`
    position: relative;
    margin: 0 0 0 40px;
`;

const BodyHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
`;

const BodyHeaderLeftSection = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    width: 171px;
    margin-left: 10px;
`;

const BodyHeaderRightSection = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-end;
    margin: 0 0 0 24px;
`;

const SearchWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 12px 0 0;
    width: 100%;
    min-width: 200px;
    max-width: 320px;
`;

const SearchInputBox = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1px 4px;
    width: 100%;
    height: 28px;
    background-color: var(--color-White);
    border: solid 1px var(--color-Line);
    border-radius: 4px;

    &:focus-within {
        border: solid 1px var(--color-Key);
    }
`;

const SearchInput = styled.input`
    padding: 0 8px;
    width: calc(100% - 24px);
    height: 24px;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-Black);
    background-color: transparent;
    border: transparent;
    outline: var(--color-Key);
`;

const SearchButtonWrap = styled.div`
    display: flex;
    align-items: center;
    height: 24px;
`;

const FavoriteWrap = styled.div`
    display: flex;
    align-items: center;
`;

const BlankView = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const AddInfoSection = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: auto;
    margin-bottom: 24px;
`;

const AddInfoBox = styled.div`
    flex: 1;
    display: flex;
    justify-content: space-between;
    padding: 16px;
    width: 100%;
    border-radius: 8px;
    background-color: var(--color-InfoBox);

    ${(props) =>
        props.$device.isPC &&
        css`
            padding: 24px;
        `};
`;

const AddInfoItem = styled.div`
    width: 50%;
    max-width: calc(50% - 8px);
`;

const AddInfoItemRow = styled.div`
    display: flex;
    align-items: center;
    margin: 0 0 8px 6px;
    width: 100%;

    &:last-child {
        margin: 0 0 0 6px;
    }
`;

const AddInfoSubItemWrap = styled.div`
    margin: 0 0 8px 32px;
`;

const AddInfoSubItem = styled.div`
    display: flex;
    align-items: center;
`;

const CheckIconStyle = {
    name: "check",
    size: "14",
    color: "var(--color-DarkKey)",
};

const ItemTextStyle = {
    margin: "0 0 0 8px",
    variant: "body-lg-400",
    color: "var(--color-SubBlack)",
};

const SubItemTextStyle = {
    margin: "0 0 4px 4px",
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
};

const defaultSearchParams = {
    page_size: 10,
    page: 1,
    is_favorite: false,
    search: "",
    ordering: "",
};

const EMPTY_NOTES = {
    count: 0,
    results: [],
};

const PAGE_KEY = "noteList";

function getPreviousPathname(currentPathname) {
    const history = JSON.parse(sessionStorage.getItem("session-page-history") || "[]");
    if (history.length === 0) return null;
    if (history[history.length - 1] === currentPathname) {
        return history.length < 2 ? null : history[history.length - 2];
    }
    return history[history.length - 1];
}

function NoteListPage() {
    const { space_uuid } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const device = useDevice();
    const { setToast } = useToast();
    const userQuery = useUser();
    const user = useMemo(() => userQuery.data || {}, [userQuery.data]);
    const updateNoteMutation = useNoteUpdate();
    const deleteBulkNoteMutation = useNoteBulkDelete();

    const [searchFilter, setSearchFilter] = useState("");
    const [searchParams, setSearchParams] = useState(() =>
        location.state ? { ...defaultSearchParams, ...location.state } : { ...defaultSearchParams },
    );
    const noteQuery = useNotes(searchParams);
    const {
        data: notes,
        isLoading: isNotesLoading,
        isRefreshing: isNotesRefreshing,
    } = useStableQueryData(noteQuery, EMPTY_NOTES);
    const [addNoteModal, setAddNoteModal] = useState(false);
    const [navigatorFolderModal, setNavigatorFolderModal] = useState(false);
    const [navigatorTagModal, setNavigatorTagModal] = useState(false);
    const rerenderTable = React.useReducer(() => ({}), {})[1];
    const checkedNotesRef = useRef([]);
    const tableRef = useRef();
    const searchParamsRef = useRef({});

    useLayoutEffect(() => {
        if (location.state) {
            setSearchParams({ ...defaultSearchParams, ...location.state });
        }
    }, [location.state]);

    useEffect(() => {
        searchParamsRef.current = { ...searchParams };
    }, [searchParams]);

    const saveSessionCache = useCallback((params = searchParamsRef.current) => {
        sessionStorage.setItem(
            PAGE_KEY,
            JSON.stringify({
                searchParams: params,
            }),
        );
    }, []);

    const loadSessionCache = useCallback(() => {
        const sessionCacheJson = sessionStorage.getItem(PAGE_KEY);
        if (sessionCacheJson) {
            const sessionCacheObj = JSON.parse(sessionCacheJson);
            setSearchParams(sessionCacheObj.searchParams);
        }
    }, []);

    const clearSessionCache = useCallback(() => {
        sessionStorage.removeItem(PAGE_KEY);
    }, []);

    useEffect(() => {
        const prevPage = getPreviousPathname(location.pathname);
        if (prevPage && prevPage.startsWith(`/${space_uuid}/note/`)) {
            loadSessionCache();
            queryClient.invalidateQueries({ queryKey: "notes" });
            clearSessionCache();
        }
    }, [clearSessionCache, loadSessionCache, location.pathname, queryClient, space_uuid]);

    const handleCheckboxAllChange = (event) => {
        checkedNotesRef.current = event.target.checked ? notes?.results?.map((note) => note.id) : [];
        rerenderTable();
    };

    const handleCheckboxChangeById = useCallback(
        (id) => {
            const index = checkedNotesRef.current.indexOf(id);
            if (index > -1) {
                checkedNotesRef.current = checkedNotesRef.current.filter((item) => item !== id);
            } else {
                checkedNotesRef.current = [...checkedNotesRef.current, id];
            }
            rerenderTable();
        },
        [rerenderTable],
    );

    const handleCheckboxChange = useCallback(
        (event) => {
            handleCheckboxChangeById(Number(event.target.id));
        },
        [handleCheckboxChangeById],
    );

    const handleExecuteCheckedNotesAction = (checkedNotesAction) => {
        if (isReadonlyUser(user)) {
            setToast("읽기 전용 사용자 계정은 해당 기능을 사용할 수 없습니다.", "warn");
            return;
        }
        if (checkedNotesAction === "delete") {
            Confirm("warn", "삭제", "선택 항목을 삭제하시겠습니까?", "삭제", () => {
                deleteBulkNoteMutation.mutate(
                    { ids: checkedNotesRef.current },
                    {
                        onSuccess: () => {
                            setToast("성공적으로 삭제되었습니다.", "info");
                            if (notes.results.length === checkedNotesRef.current.length && searchParams.page > 1) {
                                setSearchParams({
                                    ...searchParams,
                                    page: searchParams.page - 1,
                                });
                            }
                            checkedNotesRef.current = [];
                        },
                    },
                );
            });
            return;
        }
        if (checkedNotesAction === "folder") {
            setNavigatorFolderModal(true);
            return;
        }
        if (checkedNotesAction === "tag") {
            setNavigatorTagModal(true);
        }
    };

    const getCheckedNoteResources = () =>
        checkedNotesRef.current.map((id) => ({
            resource_type: "note",
            id,
        }));

    const [tableConfig, setTableConfig] = useState(
        localStorage.getItem(PAGE_KEY) ? JSON.parse(localStorage.getItem(PAGE_KEY)).note || [] : [],
    );

    const handleFavoriteClick = useCallback(
        (event, note) => {
            event.stopPropagation();
            updateNoteMutation.mutate(
                {
                    id: note.id,
                    is_favorite: !note.is_favorite,
                },
                {
                    onSuccess: () => {
                        rerenderTable();
                    },
                },
            );
        },
        [rerenderTable, updateNoteMutation],
    );

    const columns = useMemo(
        () => [
            {
                id: "id",
                accessorKey: "id",
                header: null,
                name: "선택",
                size: 36,
                minSize: 36,
                maxSize: 36,
                enableResizing: false,
                content: (info) => {
                    const value = info.getValue();
                    return (
                        <Checkbox
                            id={value}
                            checked={checkedNotesRef.current.includes(value)}
                            onChange={handleCheckboxChange}
                        />
                    );
                },
            },
            {
                id: "is_favorite",
                accessorKey: "is_favorite",
                header: null,
                name: "즐겨찾기",
                size: 36,
                minSize: 36,
                maxSize: 36,
                enableResizing: false,
                defaultVisibility: true,
                content: (info) => {
                    return (
                        <Button
                            onlyIcon
                            onClick={(event) => handleFavoriteClick(event, info.row.original)}
                            bgColor={"transparent"}
                            hoverBgColor={"transparent"}
                            disabled={isReadonlyUser(user)}
                        >
                            {info.row.original.is_favorite ? (
                                <Icon name={"bookmark"} color={"var(--color-Key)"} size={"16px"} />
                            ) : (
                                <Icon name={"bookmarkDisactive"} size={"16px"} />
                            )}
                        </Button>
                    );
                },
            },
            {
                id: "display_name",
                accessorKey: "display_name",
                name: "제목",
                size: getColumnSize("display_name", tableConfig) || 250,
                text: (info) => {
                    return info.getValue();
                },
            },
            {
                id: "folder",
                accessorKey: "folder",
                name: "folder",
                size: getColumnSize("folder", tableConfig) || 120,
                text: (info) => info.getValue()?.name || "",
            },
            {
                id: "tag_mappings",
                accessorKey: "tag_mappings",
                name: "tag",
                size: getColumnSize("tag_mappings", tableConfig) || 160,
                text: (info) => (info.getValue() || []).map((tag) => tag.name).join(", "),
            },
            {
                id: "plain_text_content",
                accessorKey: "plain_text_content",
                name: "내용",
                style: { justifyContent: "left" },
                ...(getColumnSize("plain_text_content", tableConfig) && {
                    size: getColumnSize("plain_text_content", tableConfig),
                }),
                text: (info) => {
                    return info.getValue();
                },
            },
            {
                id: "created_at",
                accessorKey: "created_at",
                name: "생성일시",
                size: getColumnSize("created_at", tableConfig) || 100,
                text: (info) => {
                    return moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss");
                },
            },
            {
                id: "updated_at",
                accessorKey: "updated_at",
                name: "수정일시",
                size: getColumnSize("updated_at", tableConfig) || 100,
                text: (info) => {
                    return moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss");
                },
            },
        ],
        [handleCheckboxChange, handleFavoriteClick, tableConfig, user],
    );

    useEffect(() => {
        const nextConfig = compareColumns(columns, [...tableConfig]);
        const hasConfigChanged = JSON.stringify(nextConfig) !== JSON.stringify(tableConfig);

        if (hasConfigChanged) {
            setTableConfig(nextConfig);
            return;
        }

        localStorage.setItem(
            PAGE_KEY,
            JSON.stringify({
                note: tableConfig,
            }),
        );
    }, [columns, tableConfig]);

    const handleToggleFavorite = () => {
        setSearchParams({ ...searchParams, is_favorite: !searchParams.is_favorite, page: 1 });
    };

    const handlePageChange = (newPage) => {
        setSearchParams({ ...searchParams, page: newPage });
    };

    const handlePageSizeChange = (newSize) => {
        setSearchParams({ ...searchParams, page_size: newSize, page: 1 });
    };

    const handleSortChange = (sort) => {
        const sortValue = sort
            .map((sortItem) => {
                return sortItem.desc ? `-${sortItem.id}` : sortItem.id;
            })
            .join(",");
        setSearchParams({ ...searchParams, ordering: sortValue, page: 1 });
    };

    useEffect(() => {
        setSearchFilter(searchParams.search);
    }, [searchParams.search]);

    useEffect(() => {
        checkedNotesRef.current = [];
        rerenderTable();
    }, [rerenderTable, noteQuery.data?.results]);

    const renderBodyHeader = () => (
        <BodyHeader>
            <BodyHeaderLeftSection>
                <Checkbox
                    margin={"0 20px 0 0"}
                    indeterminate={
                        checkedNotesRef.current.length > 0 && checkedNotesRef.current.length < notes?.results?.length
                    }
                    checked={notes?.results?.length > 0 && checkedNotesRef.current.length === notes?.results?.length}
                    onChange={handleCheckboxAllChange}
                />
                <SelectBox
                    width={"auto"}
                    height={"28px"}
                    optionWidth={"200px"}
                    optionAlign={"left"}
                    optionMaxHeight={"auto"}
                    items={[
                        { id: "", name: "--- 액션을 선택하세요. ---" },
                        { id: "delete", name: "선택된 항목을 삭제합니다." },
                        { id: "folder", name: "선택된 항목을 다른 폴더로 이동합니다." },
                        { id: "tag", name: "선택된 항목에 태그를 추가합니다." },
                    ]}
                    textAlign={"left"}
                    value={""}
                    onChange={(value) => {
                        if (checkedNotesRef.current.length === 0) {
                            setToast("선택된 항목이 없습니다.", "warn");
                            return;
                        }
                        handleExecuteCheckedNotesAction(value);
                    }}
                />
                {checkedNotesRef.current.length > 0 && (
                    <Text margin="0 0 0 10px" variant="body-sm-400" fontWeight="300">
                        {checkedNotesRef.current.length}개의 항목이 선택됨
                    </Text>
                )}
            </BodyHeaderLeftSection>
            <BodyHeaderRightSection>
                <SearchWrap>
                    <SearchInputBox>
                        <SearchInput
                            value={searchFilter}
                            onChange={(event) => setSearchFilter(event.target.value)}
                            onKeyDown={(event) => {
                                genericEnterKeyDown(event, () => {
                                    setSearchParams({ ...searchParams, search: searchFilter, page: 1 });
                                });
                            }}
                        />
                        <SearchButtonWrap>
                            {searchFilter && (
                                <Tooltip message={"초기화"}>
                                    <Button
                                        onlyIcon
                                        bgColor={"var(--color-White)"}
                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                        onClick={() => {
                                            setSearchFilter("");
                                            setSearchParams({ ...searchParams, search: "", page: 1 });
                                        }}
                                    >
                                        <Icon name={"x"} color={"var(--color-SubBlack)"} />
                                    </Button>
                                </Tooltip>
                            )}
                            <Tooltip message={"검색"}>
                                <Button
                                    onlyIcon
                                    bgColor={"var(--color-White)"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    onClick={() => {
                                        setSearchParams({
                                            ...searchParams,
                                            search: searchFilter,
                                            page: 1,
                                        });
                                    }}
                                >
                                    <Icon name={"search"} size={"12"} color={"var(--color-SubBlack)"} />
                                </Button>
                            </Tooltip>
                        </SearchButtonWrap>
                    </SearchInputBox>
                </SearchWrap>
                <FavoriteWrap>
                    <Tooltip message={"즐겨찾기만 보기"}>
                        <Button
                            onlyIcon
                            aria-label={"즐겨찾기만 보기"}
                            buttonSize={"24px"}
                            bgColor={"var(--color-White)"}
                            hoverBgColor={"none"}
                            border={
                                searchParams.is_favorite
                                    ? "solid 2px var(--color-Key)"
                                    : "solid 2px var(--color-DisabledButton)"
                            }
                            onClick={handleToggleFavorite}
                        >
                            <Icon
                                name={"bookmark"}
                                size={"12px"}
                                color={searchParams.is_favorite ? "var(--color-Key)" : "var(--color-DisabledButton)"}
                            />
                        </Button>
                    </Tooltip>
                </FavoriteWrap>
            </BodyHeaderRightSection>
        </BodyHeader>
    );

    return (
        <LoginUserLayout>
            <HtmlHead title={"연구노트"} />
            <Breadcrumb>
                <HeaderLeftWrap>
                    <BreadcrumbTitle>연구노트</BreadcrumbTitle>
                    <CountTagItem>
                        <TagText>{notes.count}건</TagText>
                    </CountTagItem>
                </HeaderLeftWrap>
                <HeaderRightWrap>
                    <Button
                        onClick={() => setAddNoteModal(!addNoteModal)}
                        width={"132px"}
                        height={"32px"}
                        bgColor={"var(--color-Button2)"}
                        hoverBgColor={"var(--color-ButtonHover2)"}
                        border={"solid 2px transparent"}
                        fontColor={"var(--color-White)"}
                        buttonText={"연구노트 추가"}
                        disabled={isReadonlyUser(user)}
                    >
                        <Icon name={"plus"} size={"12"} color={"var(--color-White)"} />
                    </Button>
                </HeaderRightWrap>
            </Breadcrumb>

            <BodyWithBreadcrumb>
                {isNotesLoading ? null : notes?.count !== 0 ? (
                    <>
                        {renderBodyHeader()}
                        <Table
                            ref={tableRef}
                            data={notes?.results || []}
                            columns={columns}
                            useSizingColumns={true}
                            useSortingColumns={true}
                            useOrderColumns={true}
                            pagination={{
                                pageIndex: searchParams.page,
                                pageSize: searchParams.page_size,
                                count: notes?.count,
                            }}
                            setPagination={(page) => {
                                if (Number(searchParams.page_size) !== Number(page.pageSize)) {
                                    handlePageSizeChange(page.pageSize);
                                } else if (searchParams.page !== Number(page.pageIndex)) {
                                    handlePageChange(page.pageIndex);
                                }
                            }}
                            sorting={
                                searchParams?.ordering
                                    ? [
                                          {
                                              id: searchParams.ordering.replace("-", ""),
                                              desc: searchParams.ordering.startsWith("-"),
                                          },
                                      ]
                                    : []
                            }
                            setSorting={handleSortChange}
                            onRowClick={(row) => {
                                saveSessionCache(searchParams);
                                navigate(`/${space_uuid}/note/${row.original.note_no}`);
                            }}
                            onRowDragStart={(event, row) => {
                                event.dataTransfer.setData(
                                    "application/json",
                                    JSON.stringify({
                                        resources: [{ resource_type: "note", id: row.original.id }],
                                    }),
                                );
                            }}
                            onSizeChange={(changedColumn) => {
                                Object.keys(changedColumn).forEach((key) => {
                                    setTableConfig((prev) =>
                                        prev.map((item) =>
                                            item.id === key ? { ...item, size: changedColumn[key] } : item,
                                        ),
                                    );
                                });
                            }}
                            columnVisibility={getColumnVisibility(tableConfig)}
                            columnOrder={getColumnOrder(tableConfig)}
                            setColumnOrder={(oldIndex, newIndex) =>
                                setTableConfig(updateColumnOrder(tableConfig, oldIndex, newIndex))
                            }
                            checkedRowIds={checkedNotesRef.current}
                            isRefreshing={isNotesRefreshing}
                        />
                    </>
                ) : searchParams.search || searchParams.is_favorite ? (
                    <>
                        {renderBodyHeader()}
                        <BlankView>
                            <Text variant="body-lg-400" margin="0 0 16px 0">
                                일치하는 연구노트가 없습니다.
                            </Text>
                        </BlankView>
                    </>
                ) : (
                    <>
                        <BlankView>
                            <Text variant="body-lg-400" margin="0 0 24px 0">
                                아직 연구노트가 없습니다.
                            </Text>
                            <Text margin="0 0 4px 0" variant="body-lg-400" textAlign="center">
                                <strong>[연구노트 추가]</strong> 버튼을 선택하여 새 연구노트를 추가하세요.
                            </Text>
                        </BlankView>
                        <AddInfoSection>
                            <AddInfoBox $device={device}>
                                <AddInfoItem>
                                    <Text margin="0 0 16px 0" variant="body-lg-400" fontWeight="600">
                                        연구노트를 추가하면 다음 기능들을 이용할 수 있습니다.
                                    </Text>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>연구노트 텍스트 작성</Text>
                                    </AddInfoItemRow>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>연구노트 목록 관리, 검색</Text>
                                    </AddInfoItemRow>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>연구노트별 참고문헌 인용 관리</Text>
                                    </AddInfoItemRow>
                                </AddInfoItem>
                                <AddInfoItem>
                                    <Text margin="0 0 16px 0" variant="body-lg-400" fontWeight="600">
                                        연구노트에서 다음의 기능들을 이용할 수 있습니다.
                                    </Text>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>실시간 공동 편집</Text>
                                    </AddInfoItemRow>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>히스토리 저장 및 복구</Text>
                                    </AddInfoItemRow>
                                    <AddInfoSubItemWrap>
                                        <AddInfoSubItem>
                                            <Text variant="body-lg-400" color="var(--color-SubBlack)">
                                                &#183;
                                            </Text>
                                            <Text {...SubItemTextStyle}>현재 상태 저장, 복구 지점 관리</Text>
                                        </AddInfoSubItem>
                                    </AddInfoSubItemWrap>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>노트에 인용문 추가 및 인용스타일 관리</Text>
                                    </AddInfoItemRow>
                                </AddInfoItem>
                            </AddInfoBox>
                        </AddInfoSection>
                    </>
                )}
            </BodyWithBreadcrumb>
            {addNoteModal && (
                <NoteAddModal
                    exitModal={() => setAddNoteModal(!addNoteModal)}
                    onSuccess={(data) => {
                        saveSessionCache(searchParams);
                        navigate(`/${space_uuid}/note/${data.note_no}`);
                    }}
                />
            )}
            {navigatorFolderModal && (
                <NavigatorFolderEditModal
                    resources={getCheckedNoteResources()}
                    exitModal={() => setNavigatorFolderModal(false)}
                />
            )}
            {navigatorTagModal && (
                <NavigatorTagMultiAddModal
                    resources={getCheckedNoteResources()}
                    exitModal={() => setNavigatorTagModal(false)}
                />
            )}
        </LoginUserLayout>
    );
}

export default NoteListPage;
